import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Input } from '@actual-app/components/input';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { getCurrency } from '@actual-app/core/shared/currencies';
import { css } from '@emotion/css';

import { Checkbox } from '#components/forms';
import { useAccounts } from '#hooks/useAccounts';
import {
  useCurrencyOptions,
  useCurrencyTranslations,
} from '#hooks/useCurrencyOptions';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { Column, Setting } from './UI';

type SymbolOverrideRowProps = {
  code: string;
};

/** One editable "code -> display symbol" line; blank restores the default. */
function SymbolOverrideRow({ code }: SymbolOverrideRowProps) {
  const { t } = useTranslation();
  const names = useCurrencyTranslations();
  const defaultSymbol = getCurrency(code).symbol;
  const [override, setOverride] = useSyncedPref(`currencySymbol-${code}`);
  const [draft, setDraft] = useState(override ?? '');

  const commit = (value: string) => {
    const next = value.trim();
    const stored = next === '' || next === defaultSymbol ? '' : next;
    setDraft(stored);
    if ((override ?? '') !== stored) {
      setOverride(stored);
    }
  };

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <Text style={{ width: 210 }}>
        {code} - {names.get(code) ?? getCurrency(code).name}
      </Text>
      <Input
        aria-label={t('Symbol for {{code}}', { code })}
        value={draft}
        placeholder={defaultSymbol}
        onChangeValue={setDraft}
        onUpdate={commit}
        onEnter={commit}
        style={{ width: 80 }}
      />
      {override ? (
        <Text style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
          {t('default: {{symbol}}', { symbol: defaultSymbol })}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Symbol overrides for the budget currency and every currency an account
 * uses, so e.g. an ARS account can show "A$" next to a USD budget's "$".
 */
function CurrencySymbolSettings({
  budgetCurrency,
}: {
  budgetCurrency: string;
}) {
  const { data: accounts = [] } = useAccounts();
  const codes = useMemo(() => {
    const set = new Set<string>([budgetCurrency]);
    for (const account of accounts) {
      if (account.currency) {
        set.add(account.currency);
      }
    }
    set.delete('');
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [accounts, budgetCurrency]);

  return (
    <View style={{ gap: 6 }}>
      <Text style={{ fontWeight: 500 }}>
        <Trans>Currency symbols</Trans>
      </Text>
      <Text style={{ color: theme.pageTextSubdued, fontSize: 13 }}>
        <Trans>
          Change how a currency's symbol is displayed, for example to tell apart
          two currencies that both use "$". Leave blank for the default.
        </Trans>
      </Text>
      {codes.map(code => (
        <SymbolOverrideRow key={code} code={code} />
      ))}
    </View>
  );
}

export function CurrencySettings() {
  const { t } = useTranslation();

  const currencyOptions = useCurrencyOptions();

  const [defaultCurrencyCode, setDefaultCurrencyCodePref] = useSyncedPref(
    'defaultCurrencyCode',
  );
  const selectedCurrencyCode = defaultCurrencyCode || '';

  const [symbolPosition, setSymbolPositionPref] = useSyncedPref(
    'currencySymbolPosition',
  );
  const [spaceEnabled, setSpaceEnabledPref] = useSyncedPref(
    'currencySpaceBetweenAmountAndSymbol',
  );
  const [, setNumberFormatPref] = useSyncedPref('numberFormat');
  const [, setHideFractionPref] = useSyncedPref('hideFraction');
  const [sidebarConverted, setSidebarConvertedPref] = useSyncedPref(
    'sidebarBalancesInBudgetCurrency',
  );

  const selectButtonClassName = css({
    '&[data-hovered]': {
      backgroundColor: theme.buttonNormalBackgroundHover,
    },
  });

  const handleCurrencyChange = (code: string) => {
    setDefaultCurrencyCodePref(code);
    if (code !== '') {
      const cur = getCurrency(code);
      setNumberFormatPref(cur.numberFormat);
      setHideFractionPref(cur.decimalPlaces === 0 ? 'true' : 'false');
      setSpaceEnabledPref(cur.symbolFirst ? 'false' : 'true');
      setSymbolPositionPref(cur.symbolFirst ? 'before' : 'after');
    }
  };

  const symbolPositionOptions = useMemo(() => {
    const selectedCurrency = getCurrency(selectedCurrencyCode);
    const symbol = selectedCurrency.symbol || '$';
    const space = spaceEnabled === 'true' ? ' ' : '';

    return [
      {
        value: 'before',
        label: `${t('Before amount')} (${t('e.g.')} ${symbol}${space}100)`,
      },
      {
        value: 'after',
        label: `${t('After amount')} (${t('e.g.')} 100${space}${symbol})`,
      },
    ];
  }, [selectedCurrencyCode, spaceEnabled, t]);

  return (
    <Setting
      primaryAction={
        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5em',
            width: '100%',
          }}
        >
          <View style={{ display: 'flex', flexDirection: 'row', gap: '1.5em' }}>
            <Column title={t('Default Currency')}>
              <Select
                value={selectedCurrencyCode}
                onChange={handleCurrencyChange}
                options={currencyOptions}
                className={selectButtonClassName}
                style={{ width: '100%' }}
              />
            </Column>

            <Column
              title={t('Symbol Position')}
              style={{
                visibility: selectedCurrencyCode === '' ? 'hidden' : 'visible',
              }}
            >
              <Select
                value={symbolPosition || 'before'}
                onChange={value => setSymbolPositionPref(value)}
                options={symbolPositionOptions.map(f => [f.value, f.label])}
                className={selectButtonClassName}
                style={{ width: '100%' }}
                disabled={selectedCurrencyCode === ''}
              />
            </Column>
          </View>

          {selectedCurrencyCode !== '' && (
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Checkbox
                id="settings-spaceEnabled"
                checked={spaceEnabled === 'true'}
                onChange={e =>
                  setSpaceEnabledPref(e.target.checked ? 'true' : 'false')
                }
              />
              <label
                htmlFor="settings-spaceEnabled"
                style={{ marginLeft: '0.5em' }}
              >
                <Trans>Add space between amount and symbol</Trans>
              </label>
            </View>
          )}

          {selectedCurrencyCode !== '' && (
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Checkbox
                id="settings-sidebarBalancesInBudgetCurrency"
                checked={sidebarConverted === 'true'}
                onChange={e =>
                  setSidebarConvertedPref(e.target.checked ? 'true' : 'false')
                }
              />
              <label
                htmlFor="settings-sidebarBalancesInBudgetCurrency"
                style={{ marginLeft: '0.5em' }}
              >
                <Trans>
                  Show foreign-currency account balances in the sidebar and
                  account list converted to the budget currency (marked with
                  "≈", using the latest exchange rate)
                </Trans>
              </label>
            </View>
          )}

          {selectedCurrencyCode !== '' && (
            <CurrencySymbolSettings budgetCurrency={selectedCurrencyCode} />
          )}
        </View>
      }
    >
      <Text>
        <Trans>
          <strong>Currency settings</strong> affect how amounts are displayed
          throughout the application. Changing the currency will affect the
          number format, symbol position, and whether fractions are shown. These
          can be adjusted after the currency is set.
        </Trans>
      </Text>
    </Setting>
  );
}
